import React from 'react';
import { Check } from 'lucide-react';

interface StepProgressProps {
  currentStep: number;
  helperText?: string;
  steps: Array<{
    number: number;
    title: string;
    description: string;
  }>;
}

const StepProgress: React.FC<StepProgressProps> = ({ currentStep, helperText, steps }) => {
  const currentStepData = steps.find((step) => step.number === currentStep) || steps[0];
  const completion = Math.max(0, Math.min(100, Math.round((currentStep / steps.length) * 100)));

  return (
    <div className="w-full border-b border-gray-200 bg-white px-6 py-4">
      <div className="max-w-6xl mx-auto">
        <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
              Step {currentStep} of {steps.length}
            </p>
            <h2 className="mt-2 text-lg font-semibold text-text-dark">{currentStepData.title}</h2>
            <p className="mt-1 text-sm text-gray-600">
              {helperText || currentStepData.description}
            </p>
          </div>
          <div className="min-w-[220px]">
            <div className="mb-2 flex items-center justify-between text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">
              <span>Progress</span>
              <span>{completion}%</span>
            </div>
            <div className="h-2 rounded-full bg-gray-100">
              <div
                className="h-2 rounded-full bg-brand-purple transition-all duration-300"
                style={{ width: `${completion}%` }}
              />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3">
          {steps.map((step, index) => (
            <React.Fragment key={step.number}>
              <div className="flex min-w-0 items-center">
                <div className={`
                  h-10 w-10 rounded-full flex items-center justify-center font-semibold transition-all
                  ${currentStep > step.number 
                    ? 'bg-brand-purple text-white' 
                    : currentStep === step.number 
                      ? 'bg-brand-purple text-white ring-4 ring-brand-purple/20'
                      : 'bg-gray-200 text-gray-500'
                  }
                `}>
                  {currentStep > step.number ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    step.number
                  )}
                </div>
                <div className="ml-3 min-w-0">
                  <p className={`text-sm font-medium ${
                    currentStep >= step.number ? 'text-text-dark' : 'text-gray-500'
                  }`}>
                    {step.title}
                  </p>
                  <p className="truncate text-xs text-gray-500">{step.description}</p>
                </div>
              </div>
              {index < steps.length - 1 && (
                <div className={`hidden h-1 flex-1 rounded md:block ${
                  currentStep > step.number ? 'bg-brand-purple' : 'bg-gray-200'
                }`} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

export default StepProgress;
